var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { useResponsiveProps, } from '@core-front/helper';
import { InputContext, } from '@core-front/input-provider';
import ObjectFit from '@core-front/object-fit';
import { ThemeContext, useTheme, } from '@core-front/theme-provider';
const CardContext = React.createContext({
    background: 'grey',
    padding: 'medium',
});
const CardCore = (props) => {
    const { responsive } = props, rest = __rest(props, ["responsive"]);
    return responsive ? (React.createElement(CardResponsiveCore, Object.assign({}, rest, { responsive: responsive }))) : (React.createElement(CardInnerCore, Object.assign({}, rest)));
};
const CardResponsiveCore = (props) => {
    const { responsive } = props, rest = __rest(props, ["responsive"]);
    const { breakPoints } = useTheme();
    const responsiveProps = useResponsiveProps({ breakPoints, responsive });
    return React.createElement(CardInnerCore, Object.assign({}, rest, responsiveProps));
};
const CardInnerCore = (props) => {
    const [isLargeScreen, setIsLargeScreen] = React.useState(false);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [isDefaultPLaying, setDefaultPLaying] = React.useState(false);
    const { breakPoints } = React.useContext(ThemeContext);
    const { badgeTop, buttonRef, children, className, background, borderRadius = 'medium', elementRef, elevation, navigate, onClick, onMouseLeave, onMouseEnter, onTransitionEnd, external = false, target, type, url, isDisabled, style, tag, backgroundVideo, backgroundImage, videoBreakpoint = 'lg', videoOpacity = true, components: { Video, Tag, TagGroup, Badge, Image }, } = props;
    const CardTag = url ? 'a' : 'div';
    const BEM = () => {
        const classArray = ['card', `card--border-radius-${borderRadius}`];
        if (background === 'grey') {
            classArray.push(`card--${background}`);
        }
        if (type) {
            classArray.push(`card--type-${type}`);
        }
        if (url || onClick) {
            classArray.push('card--link');
        }
        if (isDisabled) {
            classArray.push('card--disabled');
        }
        if (backgroundVideo) {
            classArray.push('card--background-video');
        }
        if (backgroundVideo && videoOpacity) {
            classArray.push('card--background-video-opacity');
        }
        if (backgroundImage) {
            classArray.push('card--with-background-image');
        }
        if (elevation) {
            classArray.push(`card--elevation-${elevation}`);
        }
        if (className) {
            classArray.push(className);
        }
        if (badgeTop) {
            classArray.push('card--badge');
        }
        return classArray.join(' ');
    };
    const handleClick = React.useCallback((event) => {
        if (!external && url) {
            navigate(event);
        }
        if (onClick) {
            onClick(event);
        }
    }, [external, onClick, url, navigate]);
    const cardStateHandler = () => {
        const nextIsLargeScreen = window.matchMedia(`(min-width: ${breakPoints[videoBreakpoint]}px)`).matches;
        setDefaultPLaying(!nextIsLargeScreen);
        setIsPlaying(!nextIsLargeScreen);
        if (isLargeScreen !== nextIsLargeScreen) {
            setIsLargeScreen(nextIsLargeScreen);
        }
    };
    React.useEffect(() => {
        window.addEventListener('resize', cardStateHandler);
        return () => {
            window.removeEventListener('resize', cardStateHandler);
        };
    });
    const onMouseEnterVideo = () => {
        setDefaultPLaying(true);
        setIsPlaying(true);
    };
    const onMouseLeaveVideo = () => {
        setIsPlaying(false);
    };
    const videoBEM = (video) => {
        const classArray = ['card__video'];
        if (video.className) {
            classArray.push(video.className);
        }
        return classArray.join(' ');
    };
    const imageBEM = (image) => {
        const classArray = ['card__background-image'];
        if (image.className) {
            classArray.push(image.className);
        }
        return classArray.join(' ');
    };
    const renderTags = (tags) => {
        let content;
        if (Array.isArray(tags)) {
            content = React.createElement(TagGroup, { tags: tags, size: "small" });
        }
        else {
            const tagProps = Object.assign({ className: 'card__tag', size: 'small' }, tags);
            content = React.createElement(Tag, Object.assign({}, tagProps));
        }
        return (React.createElement("div", { className: "card__tags" }, content));
    };
    const renderCard = (input) => {
        const addHoverListeners = backgroundVideo && input.input === 'mouse';
        return (React.createElement(CardTag, { className: BEM(), href: url, onClick: handleClick, target: target, ref: buttonRef || elementRef, onMouseEnter: addHoverListeners ? onMouseEnterVideo : onMouseEnter, onMouseLeave: addHoverListeners ? onMouseLeaveVideo : onMouseLeave, onTransitionEnd: onTransitionEnd, style: style },
            backgroundVideo && (React.createElement(Video, Object.assign({ onPlay: () => null }, backgroundVideo, { implementation: "html5", className: videoBEM(backgroundVideo), playing: isPlaying, defaultPlaying: isDefaultPLaying, hideOverview: true, hideWithoutConsent: "showPoster", attributes: {
                    controls: false,
                    loop: true,
                } }))),
            backgroundImage && (React.createElement(ObjectFit, { fit: "cover", positionX: "center", positionY: "center" },
                React.createElement(Image, Object.assign({}, backgroundImage, { className: imageBEM(backgroundImage), pictureClassName: "card__background-image-picture" })))),
            tag && renderTags(tag),
            typeof badgeTop === 'object' && (React.createElement("div", { className: "card__badge-wrapper" },
                React.createElement(Badge, Object.assign({ className: "card__badge" }, badgeTop)))),
            children));
    };
    return (React.createElement(CardContext.Provider, { value: {
            background: props.background,
            padding: props.padding || 'medium',
        } },
        React.createElement(InputContext.Consumer, null, renderCard)));
};
export { CardCore as default, CardResponsiveCore, CardContext, };
